import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { setEventReminder } from "../../../redux/action";
import {
  NotificationIcon,
  RedLiveIcon,
  videoIcon2,
  whiteCheck2,
} from "../../../images";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import AddToCalendarButton from "../../ui/AddToCalendarButton";

const MeetingCard = ({ meeting }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [now, setNow] = useState(new Date());

  console.log("Meeting", meeting);

  // Support multiple naming conventions from different API endpoints
  const startDate = meeting.startDate;
  const startTime = meeting.startTime;
  const endDate = meeting.endDate;
  const endTime = meeting.endTime;

  // For recurring meetings, we use today's date to check if it's "Live"
  const isRecurring =
    meeting.recurrenceType &&
    meeting.recurrenceType.toLowerCase() !== "one-time";
  const todayStr =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0");

  const effectiveStartDate = isRecurring ? todayStr : startDate;
  const effectiveEndDate = isRecurring ? todayStr : endDate || startDate;

  const start =
    effectiveStartDate && startTime
      ? `${effectiveStartDate}T${startTime}`
      : effectiveStartDate;
  const end =
    effectiveEndDate && endTime
      ? `${effectiveEndDate}T${endTime}`
      : effectiveEndDate;

  const eventId = meeting.id;
  const callLink = meeting.call_link || meeting.callLink || meeting.link;
  const hasReminder = meeting.hasReminder;

  const reminderKey = `event_reminder_${eventId}_${effectiveStartDate || "single"}_${user?.id || "guest"}`;

  const [isReminded, setIsReminded] = useState(() => {
    if (hasReminder) return true;
    try {
      const stored = localStorage.getItem(reminderKey);
      return stored === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (hasReminder) {
      setIsReminded(true);
      try {
        localStorage.setItem(reminderKey, "true");
      } catch (e) {
        console.error(e);
      }
    }
  }, [hasReminder, reminderKey]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleJoin = () => {
    if (!callLink) return;
    try {
      window.open(callLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Failed to open call link", e);
    }
  };

  const handleRemind = async () => {
    try {
      localStorage.setItem(reminderKey, "true");
    } catch (e) {
      console.error(e);
    }

    try {
      let userId = user?.id;
      const validUserId = userId ? parseInt(userId, 10) : null;
      const validEventId = eventId
        ? parseInt(String(eventId).split("-")[0], 10)
        : null;

      const success = await dispatch(
        setEventReminder(validEventId, start || meeting.startDate, validUserId),
      );
      setIsReminded(true);

      if (success === false) {
        // Rollback on failure
        setIsReminded(false);
        try {
          localStorage.removeItem(reminderKey);
        } catch (e) {
          console.error(e);
        }
      }
    } catch (error) {
      console.error("Error dispatching setEventReminder", error);
      // Rollback on error
      setIsReminded(false);
      try {
        localStorage.removeItem(reminderKey);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Use moment for robust time comparisons
  const nowMoment = moment();
  const startMoment = isRecurring
    ? moment.utc(startTime, "HH:mm:ss").local()
    : moment.utc(`${startDate}T${startTime}`, "YYYY-MM-DDTHH:mm:ss").local();

  const endMoment = endTime
    ? isRecurring
      ? moment.utc(endTime, "HH:mm:ss").local()
      : moment
          .utc(`${endDate || startDate}T${endTime}`, "YYYY-MM-DDTHH:mm:ss")
          .local()
    : startMoment.clone().add(1, "hour");

  const isHappeningNow = nowMoment.isBetween(
    startMoment,
    endMoment,
    null,
    "[)",
  );
  const timeDiffMins = startMoment.diff(nowMoment, "minutes");
  const isWithin15Mins = timeDiffMins >= 0 && timeDiffMins <= 15;
  const isBefore15Mins = timeDiffMins > 15;
  const isEnded = nowMoment.isAfter(endMoment);

  if (isEnded) return null;

  const showJoinNow = isHappeningNow || isWithin15Mins;
  const showRemindMe = !isEnded && isBefore15Mins;

  return (
    <div className="p-4 bg-white rounded border border-[#D8D8D866] flex flex-col h-full">
      <div className="flex justify-between items-start mb-2">
        <div className="p-2 rounded-[100px] bg-[#F8D21033] w-fit">
          <p className="text-[12px] flex gap-1 items-center">
            <span className=""> ⏰</span>
            <span className="text-[12px]">
              {meeting.recurrenceType !== "none"
                ? meeting.recurrenceType.charAt(0).toUpperCase() +
                  meeting.recurrenceType.slice(1)
                : meeting.recurrenceType === "none"
                  ? "Today"
                  : ""}
              ,{" "}
              {isHappeningNow ? (
                <i>Happening Now</i>
              ) : (
                moment
                  .utc(meeting.startTime, "HH:mm:ss")
                  .local()
                  .format("h:mmA z")
              )}
            </span>
          </p>
        </div>
        {isHappeningNow && (
          <div className="flex items-center gap-1 text-[#E91E26] text-[12px] font-bold">
            <img src={RedLiveIcon} alt="live" className="w-3 h-3" />
            Live
          </div>
        )}
      </div>

      <h2 className="text-secondary font-bold text-[18px]">
        {meeting.title || "Meeting"}
      </h2>
      <p className="py-2 text-grey-300 text-[14px] flex-grow">
        {meeting.description}
      </p>

      <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full mt-4">
        {showJoinNow ? (
          <button
            className="py-2 px-4 border border-[#000000] rounded-[8px] text-[14px] leading-[24px] flex items-center gap-2 w-full justify-center bg-black text-white hover:bg-gray-800 transition-colors"
            onClick={handleJoin}
            disabled={!callLink}
            title={
              callLink ? "Open meeting link" : "Meeting link not available"
            }
          >
            <img
              src={videoIcon2}
              alt="video icon"
              className="brightness-0 invert"
            />
            Join Meeting
          </button>
        ) : showRemindMe ? (
          !isReminded ? (
            <button
              className="py-2 px-4 border border-[#000000] rounded-[8px] text-[14px] leading-[24px] flex items-center gap-2 w-full justify-center hover:bg-gray-50 transition-colors"
              onClick={handleRemind}
            >
              <img src={NotificationIcon} alt="notice icon" />
              Remind me
            </button>
          ) : (
            <button className="py-2 px-4 flex gap-2 items-center bg-[#000000] text-white rounded-[8px] text-[14px] leading-[24px] w-full justify-center">
              <img src={whiteCheck2} alt="check icon" />
              Reminder Added
            </button>
          )
        ) : null}

        <AddToCalendarButton
          title={meeting.title}
          description={meeting.description}
          callLink={callLink}
          recurrenceType={meeting.recurrenceType}
          start={meeting.startTime}
          end={meeting.endTime}
          date={effectiveStartDate}
          className="py-2 px-4 flex gap-2 items-center bg-[#000000] text-white rounded-[8px] text-[14px] leading-[24px] w-full justify-center"
        />
      </div>
    </div>
  );
};

export default MeetingCard;
